import * as Storage from './StorageService';

const legacy = {
    order: [Storage.getOrder, Storage.setOrder],
    documents: [Storage.getUploadedDocuments, Storage.setUploadedDocuments],
    paymentMethod: [Storage.getPaymentMethod, Storage.setPaymentMethod],
    createdOrder: [Storage.getCreatedOrder, Storage.setCreatedOrder],
};
const keys = [...Object.keys(legacy), 'auth'];
const read = (scope, key) => scope === 'legacy' && legacy[key]
    ? legacy[key][0]() : Storage.getScopedObject(scope, key);
const write = (scope, key, value) => scope === 'legacy' && legacy[key]
    ? legacy[key][1](value) : Storage.setScopedObject(scope, key, value);
const sameId = (a, b) => String(a ?? '') === String(b ?? '');

// Move the entire historical session before a paid order can replace it.
// If another promotion already occupies /comenzi, retain the older session
// under its order ID instead of silently overwriting either order.
export function migrateLegacyPromotion() {
    const order = Storage.getOrder();
    if (!order?.promotionFlowActive) return;
    const current = read('comenzi', 'order');
    const snapshot = Object.fromEntries(keys.map(key => [key, read('legacy', key)]));
    snapshot.auth = snapshot.auth ?? Storage.getToken();
    if (!current) {
        keys.forEach(key => write('comenzi', key, snapshot[key] ?? null));
    } else if (!sameId(current.id, order.id)) {
        Storage.setScopedObject('comenzi', `archived:${order.id}`, snapshot);
    } else if (read('comenzi', 'auth') == null) {
        write('comenzi', 'auth', snapshot.auth ?? null);
    }
    keys.forEach(key => write('legacy', key, null));
}

export function checkoutScopeForOrder(order, fallback = 'legacy') {
    if (order?.checkoutEntryPoint === 'COMENZI' || order?.promotionFlowActive === true) return 'comenzi';
    if (order?.checkoutEntryPoint === 'LEGACY') return 'legacy';
    return fallback;
}

// A session captures its scope, order identity and credentials. No request or
// callback consults the route that happens to be active when it completes.
export function createCheckoutSession(scope = 'legacy', isActive = () => true) {
    if (!['legacy', 'comenzi'].includes(scope)) throw new Error('Invalid checkout scope');
    migrateLegacyPromotion();
    let expectedId = read(scope, 'order')?.id;
    let auth = read(scope, 'auth');
    if (scope === 'legacy' && auth === undefined && expectedId != null) {
        auth = Storage.getToken() ?? null;
        write(scope, 'auth', auth);
    }
    const isCurrent = () => isActive() && sameId(read(scope, 'order')?.id, expectedId);
    const put = (key, value) => {
        if (!isCurrent()) return false;
        write(scope, key, value);
        return true;
    };
    const changeOrder = (changes) => {
        const order = read(scope, 'order');
        if (!order || !isCurrent()) return null;
        const updated = {...order, ...changes};
        write(scope, 'order', updated);
        return updated;
    };
    const clearDetails = () => ['documents', 'paymentMethod', 'createdOrder'].forEach(key => write(scope, key, null));
    const session = {
        scope,
        isCurrent,
        getOrder: () => read(scope, 'order'),
        getUploadedDocuments: () => read(scope, 'documents') ?? [],
        getPaymentMethod: () => read(scope, 'paymentMethod'),
        requestOptions: () => ({auth: auth ?? null}),
        setToken: (token) => {
            if (!isCurrent()) return false;
            auth = token ?? null;
            write(scope, 'auth', auth);
            return true;
        },
        setOrder: (id, price, transactionVerified, paymentCode, necessaryDocuments, userType,
                   details, shareable, active, fiscalDone, nextState = 'PRICE', promotion = {}) => {
            if (!isCurrent()) return false;
            if (!sameId(expectedId, id)) {
                clearDetails();
                write(scope, 'auth', null);
                auth = null;
            }
            write(scope, 'order', {id, price, transactionVerified, paymentCode, necessaryDocuments,
                userType, details, shareable, active, fiscalDone, nextState,
                promotionFlowActive: promotion.promotionFlowActive === true,
                promotionEligible: promotion.promotionEligible === true,
                quotedPrice: promotion.quotedPrice, promotionStatus: promotion.promotionStatus,
                checkoutType: promotion.checkoutType, promotionAccessToken: promotion.promotionAccessToken,
                checkoutEntryPoint: promotion.checkoutEntryPoint ?? (scope === 'comenzi' ? 'COMENZI' : 'LEGACY')});
            expectedId = id;
            return true;
        },
        addUploadedDocuments: doc => {
            const docs = read(scope, 'documents') ?? [];
            return put('documents', [...docs.filter(item => item.key !== doc.key), doc]);
        },
        setPaymentMethod: value => put('paymentMethod', value),
        updateOrderState: nextState => changeOrder({nextState}),
        verifyTransactionAndReturn: () => changeOrder({transactionVerified: true, nextState: 'DOCUMENTS'}),
        verifyFiscalInfoAndReturn: () => changeOrder({fiscalDone: true, nextState: 'PAYMENT_METHOD'}),
        updateOrderCheckout: (checkoutType, promotionStatus, price, paymentCode) => changeOrder({
            checkoutType, promotionStatus, nextState: 'SIGNATURE',
            ...(price !== undefined ? {price} : {}), ...(paymentCode !== undefined ? {paymentCode} : {})}),
        recordTeregoTalonIntake: response => changeOrder({
            teregoPromotionMode: 'TALON_UPLOAD',
            teregoTalonIntakeAccepted: response?.accepted === true,
            teregoTalonIntakeId: response?.intakeId ?? null,
            teregoTalonIntakeStatus: response?.status ?? 'ACCEPTED',
        }),
        resetSession: (orderId = expectedId) => {
            if (!isCurrent() || !sameId(expectedId, orderId)) return false;
            clearDetails();
            write(scope, 'auth', null);
            write(scope, 'order', null);
            expectedId = undefined;
            auth = null;
            return true;
        },
        resetSessionWithoutOrder: () => {
            if (!isCurrent()) return false;
            clearDetails();
            return true;
        },
        getCreatedOrder: () => {
            const value = read(scope, 'createdOrder');
            if (!value) return null;
            if (Math.ceil(Math.abs(Date.now() - new Date(value.retrievedAt)) / 60000) > 15) {
                put('createdOrder', null);
                return null;
            }
            return value;
        },
        setCreatedOrder: value => put('createdOrder', value ? {...value, retrievedAt: new Date()} : null),
    };
    session.undefineMyOrder = () => session.resetSession();
    return session;
}
